import { createContext, useContext } from 'react';

/** Feature flag names, mirroring `loupe_core::flags`. */
export const PRINT_ORDERING = 'print-ordering';
export const SCAN_EDITING = 'scan-editing';

export interface AppConfig {
  /**
   * Resolved feature flags for the *current* caller. An allowlisted flag reads
   * `true` only for the logged-in customers on its allowlist, so this map is
   * per-session and must not be cached across logins.
   */
  features: Record<string, boolean>;
  /** @deprecated Prefer `useFeature(PRINT_ORDERING)`. */
  printOrderingEnabled: boolean;
}

export interface ConfigContextValue {
  config: AppConfig;
  loading: boolean;
}

export const ConfigContext = createContext<ConfigContextValue | null>(null);

export function useConfig(): ConfigContextValue {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
}

/**
 * Is `flag` enabled for the current caller?
 *
 * Returns `false` while config is still loading, so gated UI never flashes into
 * view before the flag resolves.
 *
 * This is a rendering hint only. The server independently enforces every flag at
 * its own endpoint — hiding a button is not a security boundary.
 */
export function useFeature(flag: string): boolean {
  const { config, loading } = useConfig();
  return !loading && config.features[flag] === true;
}
